import {Component, Input, OnInit} from '@angular/core';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {DashboardNotification, DashboardNotificationAction, HeaderConfig} from '@core/types';
import {AppStateService, CompanyStateService} from '@core/states';
import {AuthStateService} from '@auth/states';
import {CONFIG, MomentDate} from '@core/utils';
import {Router} from '@angular/router';
import {NotificationService} from '@core/services';
import {NotificationTray} from '@core/components/notification-tray/notification-tray';

@Component({
  selector: 'core-header',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    NotificationTray
  ],
  templateUrl: './header.html',
  styleUrl: './header.scss',
  standalone: true,
  host: {
    class:"shadow-e1",
    style: 'display:flex;display:flex;flex-direction:column;background-color:rgba(242, 242, 242, 1)'
  }
})
export class Header implements OnInit {
  @Input()
  roleName: string = '';

  currentDate = new MomentDate();

  logoFallback = '/assets/icons/feather/image.svg';
  logoError = false;
  private lastCompanyId: string | number | null = null;
  private lastLogoRevision = 0;

  constructor(
    protected appState: AppStateService,
    protected authState: AuthStateService,
    protected companyState: CompanyStateService,
    private router: Router,
    private notificationService: NotificationService
  ) {
  }

  _config: HeaderConfig = {
    menu: true,
    company: true,
    view: true,
    search: true,
    settings: true,
    notifications: true,
    user: true,
    role: true,
    subHeader: true,
    calendar: true
  }
  showNotifications = false;
  notifications: DashboardNotification[] = [];
  notificationsLoading = false;
  notificationsError = '';
  readingNotificationIds = new Set<string | number>();
  unreadCount = 0;
  readonly notificationsPreviewLimit = 5;

  @Input()
  set config(value: HeaderConfig) {
    this._config = {...this._config, ...value};
  }

  toggleMenu() {
    this.appState.toggleSidebar();
  }

  canSelectCompany(): boolean {
    return this.companyState.memberships().length > 1;
  }

  handleCompanyClick() {
    if (!this.canSelectCompany()) return;
    this.router.navigate(['/multi-user']);
  }

  getCompanyLogoUrl(companyId?: string | number | null): string {
    if (!companyId) return this.logoFallback;
    const revision = this.companyState.logoRevision();
    return `${CONFIG.BASE_API_URL}api/public/companies/${companyId}/logo?v=${revision}`;
  }

  hasCompanyLogo(): boolean {
    return !!this.getCompanyId() && !this.logoError;
  }

  getCompanyId(): string | number | null {
    const companyId = this.companyState.membership()?.company?.id ?? null;
    const revision = this.companyState.logoRevision();
    if (this.lastCompanyId !== companyId || this.lastLogoRevision !== revision) {
      this.logoError = false;
      this.lastCompanyId = companyId;
      this.lastLogoRevision = revision;
    }
    return companyId;
  }

  handleLogoError() {
    this.logoError = true;
  }

  getCompanyName(): string {
    return this.companyState.membership()?.company?.name || '';
  }

  ngOnInit() {
    if (this._config.notifications) {
      this.loadNotifications();
    }
  }

  toggleNotifications() {
    this.showNotifications = !this.showNotifications;
    if (this.showNotifications) {
      this.loadNotifications();
    }
  }

  async loadNotifications() {
    this.notificationsLoading = true;
    this.notificationsError = '';
    const response = await this.notificationService.getNotifications(1, 20, {skipLoader: true});
    if (!response.success || !response.result) {
      this.notificationsError = response.message || 'No se pudieron cargar las notificaciones.';
      this.notificationsLoading = false;
      return;
    }
    this.notifications = response.result;
    this.unreadCount = this.notifications.filter(item => !item.isRead && !item.readAt).length;
    this.notificationsLoading = false;
  }

  get previewNotifications(): DashboardNotification[] {
    return this.notifications.slice(0, this.notificationsPreviewLimit);
  }

  async goToNotifications() {
    this.showNotifications = false;
    await this.router.navigate(['/dashboard/notifications']);
  }

  async goToMyPlan() {
    await this.router.navigate(['/dashboard/plan'], {queryParamsHandling: 'preserve'});
  }

  async markNotificationRead(notification: DashboardNotification) {
    if (!notification?.id) return;
    if (this.readingNotificationIds.has(notification.id)) return;
    this.readingNotificationIds.add(notification.id);
    try {
      await this.notificationService.markAsRead(notification.id);
      this.notifications = this.notifications.map(item => item.id === notification.id ? {
        ...item,
        isRead: true,
        readAt: item.readAt ?? new Date().toISOString()
      } : item);
      this.unreadCount = Math.max(0, this.unreadCount - 1);
    } finally {
      this.readingNotificationIds.delete(notification.id);
    }
  }

  async executeNotificationAction(payload: {notification: DashboardNotification; action: DashboardNotificationAction}) {
    if (!payload?.notification?.id || !payload.action?.slug) return;

    if (payload.action.slug === 'open-survey-response') {
      const assignmentId = payload.action.params?.['assignmentId'];
      const surveyId = payload.action.params?.['surveyId'];
      if (!assignmentId) {
        return;
      }
      this.showNotifications = false;
      await this.router.navigate([`/dashboard/surveys/pending/${assignmentId}`], {
        queryParams: surveyId ? {surveyId} : undefined
      });
      return;
    }

    await this.notificationService.executeAction(payload.notification.id, payload.action.slug);
  }
}
